/**
 * TicketDetailPage - every run of one ticket
 *
 * Features:
 * - Ticket information and link to Jira
 * - Runs, newest first, with what started each one, its outcome and cost (admin)
 * - AI usage combined across runs (admin only)
 */

import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ExternalLink, History, Calendar, DollarSign, Repeat } from 'lucide-react';
import type { TicketDetailResponse, TicketRun } from '@resolve-ai/shared';
import { ticketsApi } from '../lib/tickets-api';
import { StatusBadge, LoadingState, ErrorMessage, Card } from '../components/common';
import { AcceptanceStatus } from '../components/project';
import { UsageStats } from '../components/task';
import { formatDuration } from '../lib/date-utils';
import { formatCost } from '../lib/format-utils';
import { useAuth } from '../contexts/AuthContext';

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function runTrigger(run: TicketRun): string {
  if (run.followUpMode === 'revision') return 'Revision';
  if (run.followUpMode === 'decision') return 'Product decision';
  return 'New run';
}

function runDuration(run: TicketRun): string {
  if (!run.startedAt || !run.completedAt) return '--';
  return formatDuration(new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime());
}

function RunRow({
  run,
  projectId,
  showCost,
}: {
  run: TicketRun;
  projectId?: string;
  showCost: boolean;
}) {
  return (
    <tr className="border-t border-gray-100 dark:border-gray-700 align-top">
      <td className="py-2">
        <Link
          to={`/projects/${projectId}/bugs/${run.id}`}
          className="font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
        >
          Run {run.runNumber}
        </Link>
        <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(run.createdAt)}</p>
      </td>
      <td className="py-2 text-gray-600 dark:text-gray-400">{runTrigger(run)}</td>
      <td className="py-2">
        <StatusBadge showDot={false} status={run.status} size="sm" />
        {run.errorMessage && (
          <p
            className="mt-1 max-w-xs truncate text-xs text-red-600 dark:text-red-400"
            title={run.errorMessage}
          >
            {run.errorMessage}
          </p>
        )}
      </td>
      <td className="py-2">
        <AcceptanceStatus
          prReviewStatus={run.prReviewStatus}
          noPrReviewStatus={run.noPrReviewStatus}
        />
      </td>
      <td className="py-2 text-right text-gray-600 dark:text-gray-400">{run.prCount}</td>
      <td className="py-2 text-right text-gray-600 dark:text-gray-400">{runDuration(run)}</td>
      {showCost && (
        <td className="py-2 text-right text-gray-900 dark:text-white">
          {run.costCents === null ? '--' : formatCost(run.costCents)}
        </td>
      )}
    </tr>
  );
}

function TicketRunDates({ runs }: { runs: TicketRun[] }) {
  const firstRun = runs[runs.length - 1];
  const latestRun = runs[0];
  const hasSeveralRuns = runs.length > 1;

  return (
    <div className="flex items-center flex-wrap gap-x-6 gap-y-2 mt-4 pt-4 border-t border-gray-200/60 dark:border-gray-700/50">
      <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
        <Repeat className="w-4 h-4" />
        <span>
          {runs.length} {runs.length === 1 ? 'run' : 'runs'}
        </span>
      </div>
      {firstRun && (
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>First run {formatDate(firstRun.createdAt)}</span>
        </div>
      )}
      {latestRun && hasSeveralRuns && (
        <div className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400">
          <Calendar className="w-4 h-4" />
          <span>Last run {formatDate(latestRun.createdAt)}</span>
        </div>
      )}
    </div>
  );
}

function TicketTotalCost({ costCents }: { costCents: number }) {
  return (
    <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-200/60 dark:border-gray-700/50">
      <DollarSign className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">Total Cost Across Runs</p>
        <p className="text-lg font-semibold text-gray-900 dark:text-white">
          {formatCost(costCents)}
        </p>
      </div>
    </div>
  );
}

function TicketHeader({
  detail,
  showCost,
}: {
  detail: TicketDetailResponse | null;
  showCost: boolean;
}) {
  if (!detail) return null;

  const { ticket, runs } = detail;
  const latestRun = runs[0];

  return (
    <Card>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {/* Gradient icon container */}
          <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-indigo-500/25">
            <History className="w-6 h-6 text-white" />
          </div>
          <div>
            {ticket.ticketUrl ? (
              <Link
                to={ticket.ticketUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-bold text-gray-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-400 flex items-center gap-2"
              >
                {ticket.ticketKey}
                <ExternalLink className="w-4 h-4" />
              </Link>
            ) : (
              <h1 className="text-lg font-bold text-gray-900 dark:text-white">
                {ticket.ticketKey}
              </h1>
            )}
            <p className="text-gray-500 dark:text-gray-400">{ticket.ticketTitle || 'No title'}</p>
          </div>
        </div>
        {latestRun && <StatusBadge status={latestRun.status} size="lg" />}
      </div>

      <TicketRunDates runs={runs} />
      {showCost && detail.usage && <TicketTotalCost costCents={detail.usage.totalCostCents} />}
    </Card>
  );
}

/** Load every run of the ticket named in the route. */
function useTicketDetail(projectId?: string, ticketKey?: string) {
  const [detail, setDetail] = useState<TicketDetailResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTicket = useCallback(async () => {
    if (!projectId || !ticketKey) return;

    try {
      setIsLoading(true);
      setDetail(await ticketsApi.get(projectId, ticketKey));
      setError(null);
    } catch (err) {
      console.error('Failed to fetch ticket:', err);
      setError('Failed to load ticket');
    } finally {
      setIsLoading(false);
    }
  }, [projectId, ticketKey]);

  useEffect(() => {
    fetchTicket();
  }, [fetchTicket]);

  return { detail, isLoading, error };
}

function BackButton({
  label,
  onClick,
  className = '',
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors ${className}`}
    >
      <ArrowLeft className="w-4 h-4" />
      {label}
    </button>
  );
}

export function TicketDetailPage() {
  const { projectId, ticketKey } = useParams<{ projectId: string; ticketKey: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const { detail, isLoading, error } = useTicketDetail(projectId, ticketKey);

  const backToProject = () => navigate(projectId ? `/projects/${projectId}` : '/projects');

  if ((error || !detail) && !isLoading) {
    return (
      <div className="space-y-4">
        <BackButton label="Back to Project" onClick={backToProject} />
        <ErrorMessage message={error || 'Ticket not found'} />
      </div>
    );
  }

  const ticket = detail?.ticket;
  const runs = detail?.runs ?? [];
  const showCost = isAdmin && Boolean(detail?.usage);

  return (
    <div className="space-y-6">
      <LoadingState message="Loading ticket..." isLoading={isLoading} />
      {/* Header */}
      <div>
        <BackButton
          label={`Back to ${ticket?.project.name ?? 'Project'}`}
          onClick={backToProject}
          className="mb-4"
        />
        <TicketHeader detail={detail} showCost={showCost} />
      </div>

      {/* Runs */}
      <Card>
        <div className="flex items-center gap-3 mb-4">
          <History className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Runs</h3>
        </div>
        <div className="overflow-x-auto">
          <table aria-label="Runs" className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                <th className="font-medium pb-2">Run</th>
                <th className="font-medium pb-2">Started By</th>
                <th className="font-medium pb-2">Status</th>
                <th className="font-medium pb-2">Acceptance Status</th>
                <th className="font-medium pb-2 text-right">PRs</th>
                <th className="font-medium pb-2 text-right">Duration</th>
                {showCost && <th className="font-medium pb-2 text-right">Cost</th>}
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <RunRow key={run.id} run={run} projectId={ticket?.project.id} showCost={showCost} />
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* AI Usage across all runs (admin only) */}
      {showCost && <UsageStats usage={detail?.usage ?? null} />}
    </div>
  );
}
